import React, { useRef, useEffect, forwardRef, useImperativeHandle, useCallback, useState } from 'react';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { useDrawingHandlers, SHAPE_TOOLS } from '../hooks/useDrawingHandlers';
import { useQuestionBackground } from '../hooks/useQuestionBackground';
import { useMobileQuestionTouch } from '../hooks/useMobileQuestionTouch';
import { Canvas, fabric, Point as FabricPoint, Path, Circle } from '../lib/fabricLoader';
import type { Canvas as CanvasType } from 'fabric';
import { handleCanvasResize, trackCanvasDimensions } from '../utils/canvasResize';
import { rescaleCanvasBackground } from '../utils/canvasUtils';
import { detectPenInput } from '../utils/drawing';
import { associateStrokeWithChoice, isStrokeOnOpticalChoice, removeChoiceStrokes } from '../utils/opticalChoiceOverlay';
import { setupViewportCulling, debugViewportBounds, monitorCullingPerformance } from '../utils/viewportCulling';
import { getPointsToSmoothPath } from '../utils/drawing';
import { getObjectName } from '../utils/fabricHelpers';
import { enforceZOrder, placeObjectInCorrectLayer } from '../utils/canvasZOrder';
import type { QBTestQuestionEto } from '../lib/api/types';
// Point type is already available through Stroke interface

interface WhiteboardCanvasProps {
  isDrawingModeEnabled: boolean;
  currentTool: string;
  currentColor: string;
  currentStrokeWidth: number;
  isMobile: boolean;
  isWebViewMode: boolean;
  canvasReady: boolean;
  onCanvasReady: (canvas: CanvasType) => void;
  className?: string;
  onOpticalChoiceCircled?: (choiceIndex: number) => void;
  onOpticalChoiceEliminated?: (choiceIndex: number) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  isOpticalMode?: boolean;
  currentQuestionId?: string;
  currentQuestion?: QBTestQuestionEto | null;
  eliminatedChoices?: Record<string, string>;
  opticalAnswers?: Record<string, string>;
  onOpticalAnswerSelect?: (choice: string, questionId: string) => void;
  onOpticalChoiceEliminate?: (choice: string, questionId: string) => void;
  showAnswerFeedback?: boolean;
}

export interface WhiteboardCanvasHandle {
  canvas: CanvasType | null;
  canvasElement: HTMLCanvasElement | null;
  clearCanvas: () => void;
  exportCanvas: (format?: string) => string | null;
}

const WhiteboardCanvasComponent = (props: WhiteboardCanvasProps, ref: React.ForwardedRef<WhiteboardCanvasHandle>) => {
  // Reduce logging frequency

  
  const {
    isDrawingModeEnabled,
    currentTool,
    currentColor,
    currentStrokeWidth,
    isMobile,
    isWebViewMode,
    canvasReady,
    onCanvasReady,
    className = '',
    onOpticalChoiceCircled,      // Now properly destructured
    onOpticalChoiceEliminated,    // Now properly destructured
    onSwipeLeft,
    onSwipeRight,
    isOpticalMode = false,
    currentQuestionId,
    currentQuestion,
    eliminatedChoices = {},
    opticalAnswers = {},
    onOpticalAnswerSelect,
    onOpticalChoiceEliminate,
    showAnswerFeedback = false
  } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<CanvasType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const previousQuestionIdRef = useRef<string | undefined>(undefined);

  const { setCanvasRef, isHoveringCanvas, setIsHoveringCanvas, strokes, cleanupSinglePointStrokes } = useWhiteboardStore();
  const isDrawingRef = useRef(false);
  
  // Log when component mounts
  useEffect(() => {
   
    setIsMounted(true);
    
    // Clean up any single-point strokes on mount
    cleanupSinglePointStrokes();
    
    return () => {
     
      setIsMounted(false);
    };
  }, [cleanupSinglePointStrokes]);
  
  // Initialize question background hook
  const {
    loadQuestionBackground,
    clearQuestionBackground,
    updateOpticalChoices,
    updateHandlesVisibility,
    questionMetadata,
    setQuestionMetadata,
    updateOpticalChoicePositionsFrameBased,
    isLoadingQuestion
  } = useQuestionBackground({
    fabricCanvasRef,
    isOpticalMode: isOpticalMode || false,
    isMobile,
    showAnswerFeedback,
    opticalAnswers,
    eliminatedChoices,
    onOpticalChoiceClick: onOpticalAnswerSelect
  });
  
  // Initialize mobile question touch support
  const { isQuestionBeingManipulated } = useMobileQuestionTouch({
    fabricCanvasRef,
    isMobile,
    questionMetadata,
    setQuestionMetadata,
    updateOpticalChoicePositionsFrameBased
  });
  
  // Store questionMetadata ref for recording
  const questionMetadataRef = useRef(questionMetadata);
  const setQuestionPositionMetadata = useWhiteboardStore(state => state.setQuestionPositionMetadata);
  
  useEffect(() => {
    questionMetadataRef.current = questionMetadata;
    // Update store with metadata for each question
    if (questionMetadata) {
      Object.entries(questionMetadata).forEach(([questionId, metadata]) => {
        setQuestionPositionMetadata(questionId, metadata);
      });
    }
  }, [questionMetadata, setQuestionPositionMetadata]);
  
  // Store updateOpticalChoices ref for external access
  const updateOpticalChoicesRef = useRef(updateOpticalChoices);
  useEffect(() => {
    updateOpticalChoicesRef.current = updateOpticalChoices;
  }, [updateOpticalChoices]);
  
  // Swipe detection for mobile
  const swipeStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const MIN_SWIPE_DISTANCE = 100; // Minimum swipe distance in pixels
  const MAX_SWIPE_TIME = 300; // Maximum time for swipe in ms

  
  // Create stable references for the functions
  const loadQuestionBackgroundRef = useRef(loadQuestionBackground);
  const clearQuestionBackgroundRef = useRef(clearQuestionBackground);
  
  useEffect(() => {
    loadQuestionBackgroundRef.current = loadQuestionBackground;
    clearQuestionBackgroundRef.current = clearQuestionBackground;
  });
  
  // Load question background when question changes
  useEffect(() => {
    // Only load if the question ID actually changed
    const currentQuestionId = currentQuestion?.questionId;
    const hasQuestionChanged = previousQuestionIdRef.current !== currentQuestionId;
    
   
    
    if (!fabricCanvasRef.current || !canvasReady || !currentQuestion) return;
    
    // Only proceed if the question actually changed
    if (!hasQuestionChanged) {
     
      return;
    }
    
   
    
    // Update the previous question ID
    previousQuestionIdRef.current = currentQuestionId;
    
    // Clear ALL user drawings when question changes
    const canvas = fabricCanvasRef.current;
    const objectsToRemove = canvas.getObjects().filter(obj => {
      const isUserDrawing = (obj as any).isUserDrawing === true;
      return isUserDrawing;
    });
    
   
   
    objectsToRemove.forEach(obj => canvas.remove(obj));
    
  
    
  
    useWhiteboardStore.getState().setCurrentQuestionId(currentQuestionId || null);
    
    // Ensure canvas dimensions are properly set before loading question
    const container = containerRef.current;
    if (container && canvas) {
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      
      // Update canvas dimensions if they don't match container
      if (canvas.getWidth() !== containerWidth || canvas.getHeight() !== containerHeight) {
        // Updating canvas dimensions before question load
        
        canvas.setDimensions({
          width: containerWidth,
          height: containerHeight
        });
        canvas.calcOffset();
        canvas.requestRenderAll();
        
        // For mobile WebView, give more time for dimensions to settle
        const isWebView = isWebViewMode;
        const isMobileDevice = window.innerWidth < 768;
        const delay = isWebView && isMobileDevice ? 300 : 50;
        
        const timer = setTimeout(() => {
          loadQuestionBackgroundRef.current(currentQuestion);
          // For mobile, ensure background is properly scaled after loading
          if (isMobileDevice) {
            setTimeout(() => {
              rescaleCanvasBackground(canvas);
            }, 100);
          }
        }, delay);

        return () => clearTimeout(timer);
      }
    }
    
    // Small delay to ensure clean state
    const timer = setTimeout(() => {
      loadQuestionBackgroundRef.current(currentQuestion);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [currentQuestion?.questionId, canvasReady]); // Only depend on questionId, not the entire object
  
  // Update optical choices when answers or eliminations change
  useEffect(() => {
    if (!isOpticalMode || !currentQuestionId || !canvasReady) return;
    
    const selectedAnswer = opticalAnswers[currentQuestionId];
    updateOpticalChoices(currentQuestionId, selectedAnswer || null);
  }, [isOpticalMode, currentQuestionId, opticalAnswers, eliminatedChoices, canvasReady, updateOpticalChoices]);
  
  // Update handles visibility when tool changes
  useEffect(() => {
    if (!canvasReady) return;
    updateHandlesVisibility();
  }, [currentTool, canvasReady, updateHandlesVisibility]);
  
  // This effect is now handled by the useQuestionBackground hook's updateOpticalChoices function

  // Initialize drawing handlers
  const {
    startDrawing,
    continueDrawing,
    endDrawing,
    handleTouchStart: originalHandleTouchStart,
    handleTouchMove: originalHandleTouchMove,
    handleTouchEnd: originalHandleTouchEnd,
    isDrawing,
    shouldAllowInput
  } = useDrawingHandlers({
    fabricCanvasRef,
    canvasRef,
    isDrawingModeEnabled,
    currentTool,
    currentColor,
    currentStrokeWidth,
    isMobile,
    isOpticalMode,
    currentQuestionId,
    onOpticalChoiceCircled,
    onOpticalChoiceEliminated
  });
  
  // Wrap touch handlers to prevent drawing when manipulating questions
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    // Don't start drawing if we're manipulating a question
    if (isQuestionBeingManipulated()) {
      return;
    }
    originalHandleTouchStart(e as any);
  }, [originalHandleTouchStart, isQuestionBeingManipulated]);
  
  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    // Don't continue drawing if we're manipulating a question
    if (isQuestionBeingManipulated()) {
      return;
    }
    originalHandleTouchMove(e as any);
  }, [originalHandleTouchMove, isQuestionBeingManipulated]);
  
  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    // Don't end drawing if we're manipulating a question
    if (isQuestionBeingManipulated()) {
      return;
    }
    originalHandleTouchEnd(e as any);
  }, [originalHandleTouchEnd, isQuestionBeingManipulated]);
  
  // Note: Optical choice detection is handled in useDrawingHandlers hook
  // through onOpticalChoiceCircled and onOpticalChoiceEliminated callbacks

  // Effect to render strokes when they change
  useEffect(() => {
    if (!fabricCanvasRef.current || !canvasReady) {    
      return;
    }
    
    const canvas = fabricCanvasRef.current;
    
    // Only render strokes for current question to avoid conflicts
    // Keep single-point strokes (dots) as they are valid shapes
    const currentQuestionStrokes = strokes.filter(stroke => 
      stroke.questionId === currentQuestionId && stroke.points.length > 0
    );
    
   
    
    // Get all existing stroke paths on canvas
    const existingStrokePaths = canvas.getObjects().filter(obj => {
      const hasStrokeId = !!(obj as any).strokeId;
      const isUserDrawing = (obj as any).isUserDrawing === true;
      return hasStrokeId && isUserDrawing;
    });
    
   
    
    
    
    // Create maps for efficient lookup
    const existingStrokeIdsOnCanvas = new Set(existingStrokePaths.map(path => (path as any).strokeId));
    const currentStrokeIds = new Set(currentQuestionStrokes.map(s => s.id));
    
    // Remove strokes that belong to other questions
    existingStrokePaths.forEach(path => {
      const strokeId = (path as any).strokeId;
      const pathQuestionId = (path as any).questionId;
      
      // Remove if it belongs to a different question
      if (pathQuestionId && pathQuestionId !== currentQuestionId) {
       
        canvas.remove(path);
      }
      // Also remove if it's not in current strokes for this question
      else if (pathQuestionId === currentQuestionId && !currentStrokeIds.has(strokeId)) {
     
        canvas.remove(path);
      }
    });
    
    // Add missing strokes to canvas
    let addedCount = 0;
    currentQuestionStrokes.forEach(stroke => {
      if (!existingStrokeIdsOnCanvas.has(stroke.id)) {
        try {
          let fabricObject;
          
          if (stroke.points.length === 1) {
            // Single-point stroke - render as a small dot/circle for shape tools
            if (SHAPE_TOOLS.includes(stroke.tool as any)) {
              const point = stroke.points[0];
              const circle = new Circle({
                left: point.x,
                top: point.y,
                radius: stroke.width / 2,
                fill: stroke.color,
                stroke: stroke.color,
                strokeWidth: 0,
                selectable: false,
                evented: false
              });
              fabricObject = circle;
            } else {
              // For non-shape tools, skip single points
              return;
            }
          } else if (stroke.points.length > 1) {
            // Multiple points - render as path
            let pathData: string;
            
            // Check if this is a shape tool stroke
            if (SHAPE_TOOLS.includes(stroke.tool as any)) {
              // For shapes, use straight lines between points
              pathData = `M ${stroke.points[0].x} ${stroke.points[0].y}`;
              for (let i = 1; i < stroke.points.length; i++) {
                pathData += ` L ${stroke.points[i].x} ${stroke.points[i].y}`;
              }
              // Close the path for closed shapes
              if (stroke.tool !== 'line' && stroke.tool !== 'arrow') {
                pathData += ' Z';
              }
            } else {
              // For freehand drawing, use smooth path
              pathData = getPointsToSmoothPath(stroke.points);
            }
            
            const path = new Path(pathData, {
              stroke: stroke.color,
              strokeWidth: stroke.width,
              fill: '',
              strokeLineCap: 'round',
              strokeLineJoin: 'round',
              selectable: false,
              evented: false,
              opacity: stroke.tool === 'highlighter' ? 0.5 : 1,
              globalCompositeOperation: stroke.tool === 'eraser' ? 'destination-out' : 'source-over'
            });
            fabricObject = path;
          } else {
            return;
          }
          
          // Set stroke ID for tracking
          (fabricObject as any).strokeId = stroke.id;
          (fabricObject as any).id = stroke.id;
          (fabricObject as any).isUserDrawing = true; // Mark as user drawing
          (fabricObject as any).questionId = stroke.questionId; // Add questionId for filtering
          (fabricObject as any).tool = stroke.tool; // Add tool type for shape identification
          
          // Add to canvas
          canvas.add(fabricObject);
          addedCount++;
          
          // Use z-order utility to place in correct layer
          placeObjectInCorrectLayer(canvas, fabricObject);
        } catch (error) {
          // Silently fail
        }
      }
    });
    
    // Request render if changes were made
    if (addedCount > 0 || existingStrokePaths.length > currentQuestionStrokes.length) {
      canvas.requestRenderAll();
    }
    
    // Check for optical answer detection after strokes are added
    if (isOpticalMode && addedCount > 0) {
      // Get the most recent stroke
      const recentStrokes = currentQuestionStrokes.slice(-1);
      
      recentStrokes.forEach(stroke => {
        if (stroke.tool === 'pen' && stroke.points.length > 10) {
          // Get optical choice positions from canvas
          const opticalChoices: Array<{ id: string; x: number; y: number; width: number; height: number }> = [];
          
          canvas.getObjects().forEach(obj => {
            const objName = (obj as any).name || '';
            if (objName.startsWith('opticalChoice_')) {
              const choiceId = objName.replace('opticalChoice_', '');
              const bounds = obj.getBoundingRect();
              opticalChoices.push({
                id: choiceId,
                x: bounds.left,
                y: bounds.top,
                width: bounds.width,
                height: bounds.height
              });
            }
          });
          
          if (opticalChoices.length > 0) {
            // Import circle detection functions
            import('../utils/circleDetection').then(({ findCircledOpticalChoice, findEliminatedOpticalChoice }) => {
              // Check for selection (circle)
              const selectedChoice = findCircledOpticalChoice(stroke.points, opticalChoices);
              if (selectedChoice && onOpticalAnswerSelect) {
             
                onOpticalAnswerSelect(selectedChoice as 'A' | 'B' | 'C' | 'D' | 'E', currentQuestionId || '');
              }
              
              // Check for elimination (diagonal line)
              const eliminatedChoice = findEliminatedOpticalChoice(stroke.points, opticalChoices);
              if (eliminatedChoice && onOpticalChoiceEliminate) {
               
                onOpticalChoiceEliminate(eliminatedChoice, currentQuestionId || '');
              }
            });
          }
        }
      });
    }
  }, [strokes, canvasReady, currentQuestionId, isOpticalMode, onOpticalAnswerSelect, onOpticalChoiceEliminate]);

  // Initialize fabric canvas
  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    
    if (fabricCanvasRef.current) {
      return;
    }

    // Use a timer to ensure DOM is ready
    const initTimer = setTimeout(() => {
      try {
        // Force pen tool on initialization
        const currentStoreTool = useWhiteboardStore.getState().currentTool;
     
      
      // If somehow select is set, force it back to pen
      if (currentStoreTool === 'select') {
     
        useWhiteboardStore.getState().setCurrentTool('pen');
      }
      
      // Get container dimensions for proper sizing
      const container = containerRef.current;
      const canvasWidth = container?.clientWidth || window.innerWidth;
      const canvasHeight = container?.clientHeight || window.innerHeight;
      
      const canvas = new Canvas(canvasRef.current!, {
        isDrawingMode: false,
        selection: false, // Always start with selection off
        interactive: true,
        backgroundColor: '#ffffff',
        width: canvasWidth,
        height: canvasHeight,
        enableRetinaScaling: true,
        enablePointerEvents: true,
        allowTouchScrolling: false,
        perPixelTargetFind: true, // Daha hassas tıklama algılaması 
        targetFindTolerance: isMobile ? 20 : 5, // Increase tolerance on mobile for easier touch
        skipTargetFind: false, // Enable object detection for optical choices
        renderOnAddRemove: false, // Disable automatic rendering to have more control
        stateful: false, // Disable state tracking for better performance
        hoverCursor: `url('data:image/svg+xml;utf8,<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="%23333"/></svg>') 3 21, crosshair`,
        defaultCursor: `url('data:image/svg+xml;utf8,<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="%23333"/></svg>') 3 21, crosshair`,
        moveCursor: `url('data:image/svg+xml;utf8,<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="%23333"/></svg>') 3 21, crosshair`
      });

      fabricCanvasRef.current = canvas;
      setCanvasRef(canvas);
      
      // Reset zoom to 1 on mobile to avoid conflicts with custom pinch-to-zoom
      if (isMobile) {
        canvas.setZoom(1);
        canvas.absolutePan({ x: 0, y: 0 } as any);
      }
      
      // Update canvas size in store for recording
      const { setCanvasSize } = useWhiteboardStore.getState();
      const canvasSize = {
        width: canvasWidth,
        height: canvasHeight
      };
     
      setCanvasSize(canvasSize);
      
      // Disable object caching globally
      (fabric.Object.prototype as any).objectCaching = false;
      
      // Set up viewport culling for performance
      const cleanupViewportCulling = setupViewportCulling(canvas);
      
      // Store cleanup function on canvas for later use
      (canvas as any)._cleanupViewportCulling = cleanupViewportCulling;
      
      // Debug viewport culling in development
     
      
      // Override renderAll to catch dimension errors and implement viewport culling
      const originalRenderAll = canvas.renderAll.bind(canvas);
      const originalRender = canvas._renderObjects ? canvas._renderObjects.bind(canvas) : null;
      
      // Override internal render method to filter problematic objects and implement viewport culling
      if (originalRender) {
        (canvas as any)._renderObjects = function(ctx: CanvasRenderingContext2D, objects: any[]) {
          // First, apply viewport culling
          let objectsToRender = objects;
          
          // Check if viewport culling is available
          if ((window as any).viewportCulling) {
            const { getViewportBounds, isObjectInViewport } = (window as any).viewportCulling;
            const viewportBounds = getViewportBounds(this);
            
            objectsToRender = objects.filter((obj: any) => {
              // Always render certain critical objects
              const name = obj.get?.('name') || obj.name;
              if (name === 'questionBackground' || 
                  name === 'parentQuestionBackground' ||
                  name?.startsWith('questionOpticalChoice_') || 
                  name?.startsWith('opticalChoice_')) {
                return true;
              }
              
              // Check if object is in viewport
              return isObjectInViewport(obj, viewportBounds);
            });
            
           
          }
          
          // Then filter out problematic objects
          const validObjects = objectsToRender.filter((obj: any) => {
            // Skip objects with invalid dimensions
            if (obj.type === 'image') {
              const img = obj as any; // fabric.Image type changed in v6
              const element = img.getElement();
              
              // Skip if no element
              if (!element) return false;
              
              // Skip if element has zero dimensions
              if (element.width <= 0 || element.height <= 0) {
                return false;
              }
              
              // Skip if element is a canvas with issues
              if (element instanceof HTMLElement && element.tagName === 'CANVAS') {
                try {
                  // Try to access the canvas context to check if it's valid
                  const canvasElement = element as unknown as HTMLCanvasElement;
                  const testCtx = canvasElement.getContext('2d');
                  if (!testCtx) return false;
                } catch (e) {
                  return false;
                }
              }
            }
            return true;
          });
          
          return (originalRender as any).call(this, ctx, validObjects);
        };
      }
      
      // Add render debouncing and batching
      let renderTimer: any = null;
      let renderRequested = false;
      let renderCount = 0;
      
      const debouncedRender = () => {
        if (renderRequested) return;
        
        renderRequested = true;
        if (renderTimer) clearTimeout(renderTimer);
        
        renderTimer = setTimeout(() => {
          renderRequested = false;
          originalRenderAll.call(canvas);
         
        }, 16); // 60 FPS
      };
      
      // Override renderAll to use batching and debouncing
      canvas.renderAll = function() {
        try {
          // Skip render if nothing changed
          if ((canvas as any)._skipRender) {
            return canvas;
          }
          
          // Pre-process all objects to fix cache issues
          canvas.getObjects().forEach(obj => {
            // Disable caching for all objects to prevent render errors
            obj.objectCaching = false;
            
            // Check ellipses
            if (obj.type === 'ellipse') {
              const ellipse = obj as any;
              if (!ellipse.rx || ellipse.rx <= 0) ellipse.rx = 10;
              if (!ellipse.ry || ellipse.ry <= 0) ellipse.ry = 10;
            }
            
            // Check images
            if (obj.type === 'image') {
              const img = obj as any; // fabric.Image type changed in v6
              
              // Force disable caching for images
              img.objectCaching = false;
              (img as any).cacheKey = ''; // Clear cache key
              (img as any).statefullCache = false;
              
              // Clear any existing cache
              if ((img as any)._cacheCanvas) {
                (img as any)._cacheCanvas = null;
                (img as any)._cacheContext = null;
                img.cacheWidth = 0;
                img.cacheHeight = 0;
              }
              
              // Check element validity
              const element = img.getElement() as HTMLImageElement | HTMLCanvasElement;
              const hasValidElement = element && element.width > 0 && element.height > 0;
              const hasValidDimensions = img.width && img.height && img.width > 0 && img.height > 0;
              
              if (!hasValidElement || !hasValidDimensions) {
                // Don't hide question images, try to fix them instead
                if ((img as any).name === 'questionBackground' || 
                    (img as any).name === 'parentQuestionBackground' ||
                    (img as any).isQuestionImage) {
                  img.visible = true;
                  img.opacity = 1;
                } else {
                  img.visible = false;
                }
              } else {
                img.visible = true;
                // Ensure question images are always fully visible
                if ((img as any).name === 'questionBackground' || 
                    (img as any).name === 'parentQuestionBackground' ||
                    (img as any).isQuestionImage) {
                  img.opacity = 1;
                }
              }
            }
            
            // Check rects
            if (obj.type === 'rect') {
              const rect = obj as any; // fabric.Rect type changed in v6
              if (!rect.width || rect.width <= 0) rect.width = 1;
              if (!rect.height || rect.height <= 0) rect.height = 1;
            }
            
            // Check groups
            if (obj.type === 'group') {
              const group = obj as any; // fabric.Group type changed in v6
              group.objectCaching = false;
              
              // Ensure group has dimensions
              if (!group.width || group.width <= 0 || !group.height || group.height <= 0) {
                group.visible = false;
              } else {
                group.visible = true;
                // Disable caching for children
                group.getObjects().forEach((child: any) => {
                  child.objectCaching = false;
                  
                  if (child.type === 'ellipse') {
                    const ellipse = child as any;
                    if (!ellipse.rx || ellipse.rx <= 0) ellipse.rx = 10;
                    if (!ellipse.ry || ellipse.ry <= 0) ellipse.ry = 10;
                  }
                });
              }
            }
          });
          
          // Call original renderAll
          return (originalRenderAll as any).apply(this, arguments);
        } catch (error) {
          console.error('Canvas render error:', error);
          // Try to render without cache
          try {
            const ctx = canvas.getContext();
            canvas.clearContext(ctx);
            (canvas as any)._renderBackground(ctx);
            (canvas as any)._renderObjects(ctx, canvas.getObjects().filter((obj: any) => obj.visible));
            return canvas;
          } catch (fallbackError) {
            console.error('Fallback render also failed:', fallbackError);
            return canvas;
          }
        }
      };
      
      // Check for optical choices after a delay
      setTimeout(() => {
        const opticalCount = canvas.getObjects().filter(obj => {
          const name = obj.get?.('name') || (obj as any).name;
          return name?.startsWith('questionOpticalChoice_') || name?.startsWith('opticalChoice_');
        }).length;
     
        
        if (opticalCount === 0) {
         
        } else {
          // Make sure optical choices are evented
          canvas.getObjects().forEach(obj => {
            const name = obj.get?.('name') || (obj as any).name;
            if (name?.includes('optical')) {
              // Ensure the object has valid dimensions
              if (obj.type === 'ellipse') {
                const ellipse = obj as any;
                if (!ellipse.rx || ellipse.rx <= 0) ellipse.rx = 10;
                if (!ellipse.ry || ellipse.ry <= 0) ellipse.ry = 10;
              }
              obj.set({
                evented: true,
                selectable: false,
                hasControls: false,
                hasBorders: false
              });
             
            }
          });
          canvas.requestRenderAll();
        }
      }, 2000);
      
      // Disable mouse wheel zoom on the canvas
      canvas.on('mouse:wheel', (opt) => {
        opt.e.preventDefault();
        opt.e.stopPropagation();
        return false;
      });
      
      // Disable touch gestures on mobile to prevent interference with optical choices
      if (isMobile) {
        canvas.allowTouchScrolling = false;
        // Disable multi-touch gestures
        canvas.on('touch:gesture' as any, (opt: any) => {
          opt.e.preventDefault();
          opt.e.stopPropagation();
          return false;
        });
      }
      
      // Force setup drawing events immediately if pen mode
      if (currentStoreTool !== 'select') {
       
        canvas.selection = false;
        canvas.skipTargetFind = true;
        
        // Setup basic drawing handler
        const handleInitialMouseDown = (options: any) => {
        
          options.e.preventDefault();
          options.e.stopPropagation();
        };
        
        canvas.on('mouse:down', handleInitialMouseDown);
      }

      // Set up event handlers for object modifications
      canvas.on('object:modified', (e: any) => {
        const object = e.target;
        if (!object) return;
        const strokeId = object.get('strokeId') as string;
        if (!strokeId) return;
        
        // Record transformation event
        const { addRecordingEvent } = useWhiteboardStore.getState();
        addRecordingEvent({
          type: 'object_transform',
          timestamp: 0,
          data: {
            objectId: strokeId,
            transform: {
              left: object.left || 0,
              top: object.top || 0,
              scaleX: object.scaleX || 1,
              scaleY: object.scaleY || 1,
              angle: object.angle || 0
            }
          }
        });
      });

      canvas.on('selection:created', (e: any) => {
        const { addRecordingEvent } = useWhiteboardStore.getState();
        const selected = e.selected || [];
        const objectIds = selected.map((obj: any) => obj.get('strokeId') as string).filter(Boolean);
        
        if (objectIds.length > 0) {
          addRecordingEvent({
            type: 'object_select',
            timestamp: 0,
            data: { objectIds }
          });
        }
      });

      canvas.on('selection:updated', (e: any) => {
        const { addRecordingEvent } = useWhiteboardStore.getState();
        const selected = e.selected || [];
        const deselected = e.deselected || [];
        
        const selectedIds = selected.map((obj: any) => obj.get('strokeId') as string).filter(Boolean);
        const deselectedIds = deselected.map((obj: any) => obj.get('strokeId') as string).filter(Boolean);
        
        if (deselectedIds.length > 0) {
          addRecordingEvent({
            type: 'object_deselect',
            timestamp: 0,
            data: { objectIds: deselectedIds }
          });
        }
        
        if (selectedIds.length > 0) {
          addRecordingEvent({
            type: 'object_select',
            timestamp: 0,
            data: { objectIds: selectedIds }
          });
        }
      });

      canvas.on('selection:cleared', () => {
        const { addRecordingEvent } = useWhiteboardStore.getState();
        addRecordingEvent({
          type: 'object_deselect',
          timestamp: 0,
          data: { objectIds: [] }
        });
      });

      // Add path created event handler
      canvas.on('path:created', (e: any) => {
        const path = e.path;
        if (!path) return;
       
      });

      // Add object removed event handler
      canvas.on('object:removed', (e: any) => {
        const object = e.target;
        if (!object) return;
        
        const strokeId = object.get('strokeId') as string;
        if (strokeId) {
          // Object removed - strokeId: {strokeId}
        }
      });

      // Add mouse wheel event for zooming (desktop only)
      if (!isMobile) {
        canvas.on('mouse:wheel', (opt: any) => {
          const delta = opt.e.deltaY;
          let zoom = canvas.getZoom();
          zoom *= 0.999 ** delta;
          
          // Limit zoom
          if (zoom > 5) zoom = 5;
          if (zoom < 0.2) zoom = 0.2;
          
          canvas.zoomToPoint(new FabricPoint(opt.e.offsetX, opt.e.offsetY), zoom);
          opt.e.preventDefault();
          opt.e.stopPropagation();
        });
      }

     

      // Add mouse down/up/move events for custom handling (desktop only)
      if (!isMobile) {
        canvas.on('mouse:down', (opt: any) => {
          const evt = opt.e;
          if (evt.altKey === true) {
            (canvas as any).isDragging = true;
            canvas.selection = false;
            (canvas as any).lastPosX = evt.clientX;
            (canvas as any).lastPosY = evt.clientY;
          }
        });

        canvas.on('mouse:move', (opt: any) => {
          if ((canvas as any).isDragging) {
            const e = opt.e;
            const vpt = canvas.viewportTransform;
            if (vpt) {
              vpt[4] += e.clientX - (canvas as any).lastPosX;
              vpt[5] += e.clientY - (canvas as any).lastPosY;
              canvas.requestRenderAll();
              (canvas as any).lastPosX = e.clientX;
              (canvas as any).lastPosY = e.clientY;
            }
          }
        });

        canvas.on('mouse:up', (opt: any) => {
          // on mouse up we want to recalculate new interaction
          // for all objects, so we call setViewportTransform
          canvas.setViewportTransform(canvas.viewportTransform);
          (canvas as any).isDragging = false;
          canvas.selection = true;
        });
      }

      // Add object scaling event
      canvas.on('object:scaling', (e: any) => {
        const object = e.target;
        if (!object) return;
        
        // Optionally limit scaling
        const maxScale = 5;
        const minScale = 0.1;
        
        if (object.scaleX && object.scaleX > maxScale) {
          object.scaleX = maxScale;
        }
        if (object.scaleY && object.scaleY > maxScale) {
          object.scaleY = maxScale;
        }
        if (object.scaleX && object.scaleX < minScale) {
          object.scaleX = minScale;
        }
        if (object.scaleY && object.scaleY < minScale) {
          object.scaleY = minScale;
        }
      });

      // Add object rotating event
      canvas.on('object:rotating', (e: any) => {
        const object = e.target;
        if (!object) return;
        
        // Snap to 15 degree angles when shift is pressed
        if (e.e && e.e.shiftKey) {
          const snapAngle = 15;
          const angle = Math.round((object.angle || 0) / snapAngle) * snapAngle;
          object.angle = angle;
        }
      });

      // Track canvas dimensions for resize calculations
      let previousDimensions = trackCanvasDimensions(canvas);
      
      // Handle window resize with debouncing
      let resizeDebounceTimer: NodeJS.Timeout;
      let previousOrientation = window.innerWidth > window.innerHeight;
      
      const debouncedResizeCanvas = (immediate = false) => {
        const executeResize = () => {
          const container = containerRef.current;
          if (!container) return;
          
          const containerWidth = container.clientWidth;
          const containerHeight = container.clientHeight;
          
          // Check if orientation has changed
          const currentOrientation = window.innerWidth > window.innerHeight;
          const orientationChanged = previousOrientation !== currentOrientation;
          previousOrientation = currentOrientation;
          
          // Get current dimensions before resize
          const oldDimensions = previousDimensions;
          
          // Set new dimensions
          canvas.setDimensions({
            width: containerWidth,
            height: containerHeight
          });
          canvas.calcOffset();
          
          // Debug: Log dimension mismatch
          if (import.meta.env.DEV) {
            // Removed debug logging
          }
          
          // Handle scaling of all objects
          handleCanvasResize({
            canvas,
            oldWidth: oldDimensions.width,
            oldHeight: oldDimensions.height,
            newWidth: containerWidth,
            newHeight: containerHeight,
            isMobile
          });
          
          // Update tracked dimensions
          previousDimensions = trackCanvasDimensions(canvas);
          
          // Update canvas size in store for recording
          const { setCanvasSize } = useWhiteboardStore.getState();
          const newCanvasSize = {
            width: containerWidth,
            height: containerHeight
          };
        
          setCanvasSize(newCanvasSize);
          
          canvas.requestRenderAll();

          // If orientation changed and we have a current question, rescale the background
          if (orientationChanged && currentQuestion) {
            // Instead of reloading, just rescale the existing background
            setTimeout(() => {
              rescaleCanvasBackground(canvas);
            }, 100);
          }
        };
        
        if (immediate) {
          executeResize();
        } else {
          clearTimeout(resizeDebounceTimer);
          resizeDebounceTimer = setTimeout(executeResize, 250);
        }
      };

      const handleResize = () => debouncedResizeCanvas();
      window.addEventListener('resize', handleResize);
      
      // Initial resize - call immediately and after a short delay to ensure proper sizing
      debouncedResizeCanvas(true);  // Use immediate mode for first load
      // Also call after DOM settles for mobile
      if (isMobile) {
        setTimeout(() => {
          debouncedResizeCanvas(true);  // Use immediate mode for mobile too
        }, 100);
      }
      
      // Add native pointer event listeners for palm rejection - copied from old whiteboard
      const canvasElement = canvasRef.current;
      if (canvasElement) {
        const activePointers = new Map<number, boolean>();
        
        const handlePointerDown = (e: PointerEvent) => {
          activePointers.set(e.pointerId, detectPenInput(e));
        };
        
        const handlePointerUp = (e: PointerEvent) => {
          activePointers.delete(e.pointerId);
        };
        
        // Add native pointer event listeners
        canvasElement.addEventListener('pointerdown', handlePointerDown, { passive: true });
        canvasElement.addEventListener('pointerup', handlePointerUp, { passive: true });
        canvasElement.addEventListener('pointercancel', handlePointerUp, { passive: true });
        
        // For mobile, ensure touch events trigger fabric mouse events
        if (isMobile) {
          const handleTouchToMouse = (e: TouchEvent) => {
            if (e.touches.length === 1) {
              const touch = e.touches[0];
              const mouseEvent = new MouseEvent(e.type === 'touchstart' ? 'mousedown' : 'mouseup', {
                bubbles: true,
                cancelable: true,
                clientX: touch.clientX,
                clientY: touch.clientY,
                screenX: touch.screenX,
                screenY: touch.screenY
              });
              canvasElement.dispatchEvent(mouseEvent);
            }
          };
          
          canvasElement.addEventListener('touchstart', handleTouchToMouse, { passive: false });
          canvasElement.addEventListener('touchend', handleTouchToMouse, { passive: false });
        }
        
        // Store cleanup function
        (canvas as any)._cleanupNativeListeners = () => {
          canvasElement.removeEventListener('pointerdown', handlePointerDown);
          canvasElement.removeEventListener('pointerup', handlePointerUp);
          canvasElement.removeEventListener('pointercancel', handlePointerUp);
          // handleTouchToMouse is defined in the outer scope, no need to clean up here
        };
      }

      // Mark canvas as ready
      if (typeof onCanvasReady === 'function') {
        onCanvasReady(canvas);
      }
      
      // Trigger a re-render to ensure effects run after canvas is ready
      setCanvasRef(canvas);
      
      // Force initial resize after canvas is ready
      setTimeout(() => {
        const container = containerRef.current;
        if (container) {
          const containerWidth = container.clientWidth;
          const containerHeight = container.clientHeight;
          
          canvas.setDimensions({
            width: containerWidth,
            height: containerHeight
          });
          canvas.calcOffset();
          canvas.requestRenderAll();
        }
      }, 100);

      // Force initial canvas mode after a short delay
      setTimeout(() => {
        const tool = useWhiteboardStore.getState().currentTool;
               
        // Always force the correct mode based on the tool
        if (tool === 'select') {
          canvas.selection = true;
          canvas.defaultCursor = 'default';
          canvas.hoverCursor = 'move';
          canvas.moveCursor = 'grabbing';
        } else {
          canvas.selection = false;
          canvas.skipTargetFind = false; // Enable object detection for optical choices
          const penCursor = `url('data:image/svg+xml;utf8,<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="%23333"/></svg>') 3 21, crosshair`;
          canvas.defaultCursor = penCursor;
          canvas.hoverCursor = penCursor;
          canvas.moveCursor = penCursor;
          
          // Also ensure all objects are non-selectable and non-evented (except optical choices)
          canvas.getObjects().forEach((obj) => {
            const objName = obj.get('name') as string;
            // Keep optical choices interactive even in drawing mode
            // Check for both naming conventions (old and new)
            if (objName?.startsWith('questionOpticalChoice_') || objName?.startsWith('opticalChoice_')) {
              obj.set({
                selectable: false,
                evented: true, // Keep optical choices clickable
                hasControls: false,
                hasBorders: false
              });
            } else {
              obj.set({
                selectable: false,
                evented: false, // IMPORTANT: Disable all events
                hasControls: false,
                hasBorders: false
              });
            }
          });
        }
        canvas.requestRenderAll();
      }, 500); // Increased delay to 500ms

      return () => {
        clearTimeout(initTimer);
        clearTimeout(resizeDebounceTimer);
        window.removeEventListener('resize', handleResize);
        
        // Clean up native listeners if they exist
        if ((canvas as any)._cleanupNativeListeners) {
          (canvas as any)._cleanupNativeListeners();
        }
        
        // Clean up viewport culling
        if ((canvas as any)._cleanupViewportCulling) {
          (canvas as any)._cleanupViewportCulling();
        }
        
        // Clean up culling monitor
        if ((canvas as any)._cullingMonitor) {
          clearInterval((canvas as any)._cullingMonitor);
        }
        
        canvas.dispose();
        fabricCanvasRef.current = null;
        setCanvasRef(null);
      };
      } catch (error) {
        console.error('🎨 WhiteboardCanvas - Error initializing canvas:', error);
      }
    }, 100); // 100ms delay

    return () => {
      clearTimeout(initTimer);
    };
  }, [onCanvasReady, setCanvasRef, isMounted]); // Added isMounted to trigger after component mounts

  // Set up drawing event handlers on fabric canvas - only run after canvas is created
  useEffect(() => {
    if (!fabricCanvasRef.current) return;
    
    const canvas = fabricCanvasRef.current;

    // Add a small delay to ensure canvas is fully initialized
    const timer = setTimeout(() => {
     
    // Disable/enable canvas interaction based on tool
    if (currentTool === 'select') {
      canvas.selection = true;
      canvas.isDrawingMode = false;
      
      // Make objects selectable
      canvas.forEachObject((obj) => {
        const objName = obj.get('name') as string;
        if (objName !== 'questionBackground' && 
            objName !== 'parentQuestionBackground' &&
            objName !== 'questionFrame' &&
            objName !== 'questionResizeHandle' &&
            objName !== 'questionMoveHandle' &&
            objName !== 'questionMoveIcon' &&
            objName !== 'opticalChoice') {
          obj.set({
            selectable: true,
            evented: true,
            hasControls: true,
            hasBorders: true,
            lockMovementX: false,
            lockMovementY: false
          });
        }
      });
      
      canvas.defaultCursor = 'default';
      canvas.hoverCursor = 'move';
      canvas.moveCursor = 'move';
      if ('rotationCursor' in canvas) {
        (canvas as any).rotationCursor = 'crosshair';
      }
    } else {
      // Drawing mode
      canvas.selection = false;
      canvas.isDrawingMode = false;
      canvas.skipTargetFind = false; // Enable object detection for optical choices
      
      // Discard any active selection first
      canvas.discardActiveObject();
      
      // Make ALL objects non-selectable and non-evented (except optical choices)
      canvas.getObjects().forEach((obj) => {
        const objName = obj.get('name') as string;
        const isUserDrawing = (obj as any).isUserDrawing === true;
        
        // Skip user drawings - they already have correct settings
        if (isUserDrawing) {
          return;
        }
        
        // Keep optical choices interactive even in drawing mode
        // Check for both naming conventions (old and new)
        if (objName?.startsWith('questionOpticalChoice_') || objName?.startsWith('opticalChoice_')) {
          obj.set({
            selectable: false,
            evented: true, // Keep optical choices clickable
            hasControls: false,
            hasBorders: false
          });
        } else {
          obj.set({
            selectable: false,
            evented: false, // IMPORTANT: Disable all events on objects
            hasControls: false,
            hasBorders: false
          });
        }
      });
      
      // Request render to apply changes
      canvas.requestRenderAll();
      
      // Set cursor based on tool with custom SVG cursors
      let cursor = 'crosshair';
      switch (currentTool) {
        case 'pen':
        case 'pencil':
          cursor = `url('data:image/svg+xml;utf8,<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="%23333"/></svg>') 3 21, crosshair`;
          break;
        case 'highlighter':
          cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32" transform="rotate(45)"><rect x="8" y="4" width="8" height="20" fill="yellow" opacity="0.6" stroke="black"/><rect x="10" y="24" width="4" height="4" fill="gray"/></svg>') 0 32, crosshair`;
          break;
        case 'eraser':
          cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><rect x="2" y="8" width="20" height="12" rx="2" fill="pink" stroke="black" stroke-width="2"/><rect x="8" y="8" width="8" height="12" fill="white"/></svg>') 12 12, crosshair`;
          break;
        case 'laser':
          cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" fill="red" opacity="0.8"/><circle cx="12" cy="12" r="4" fill="darkred"/></svg>') 12 12, crosshair`;
          break;
        case 'areaEraser':
          cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" fill="none" stroke="red" stroke-width="2" stroke-dasharray="4,4"/><line x1="8" y1="8" x2="16" y2="16" stroke="red" stroke-width="2"/><line x1="16" y1="8" x2="8" y2="16" stroke="red" stroke-width="2"/></svg>') 12 12, crosshair`;
          break;
        case 'circle':
        case 'ellipse':
          cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="black" stroke-width="2" stroke-dasharray="3,3"/></svg>') 12 12, crosshair`;
          break;
        case 'rectangle':
        case 'parallelogram':
        case 'trapezoid':
          cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" fill="none" stroke="black" stroke-width="2" stroke-dasharray="3,3"/></svg>') 12 12, crosshair`;
          break;
        case 'triangle':
          cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M12 4 L4 20 L20 20 Z" fill="none" stroke="black" stroke-width="2" stroke-dasharray="3,3"/></svg>') 12 12, crosshair`;
          break;
        case 'arrow':
          cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M4 12 L16 12 M16 12 L12 8 M16 12 L12 16" fill="none" stroke="black" stroke-width="2"/></svg>') 12 12, crosshair`;
          break;
        case 'line':
          cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><line x1="4" y1="4" x2="20" y2="20" stroke="black" stroke-width="2"/></svg>') 12 12, crosshair`;
          break;
        case 'star':
          cursor = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M12 2 L15 9 L22 9 L16.5 14 L19 21 L12 17 L5 21 L7.5 14 L2 9 L9 9 Z" fill="none" stroke="black" stroke-width="2" stroke-dasharray="3,3"/></svg>') 12 12, crosshair`;
          break;
        default:
          cursor = 'crosshair';
      }
      
      canvas.defaultCursor = cursor;
      canvas.hoverCursor = cursor;
      canvas.moveCursor = cursor;
    }
    
      canvas.requestRenderAll();
    }, 100); // Small delay

    return () => clearTimeout(timer);
  }, [isDrawingModeEnabled, currentTool]);

  // Set up fabric canvas drawing events - only run after canvas is created
  useEffect(() => {
    // Fabric events setup
    
    if (!fabricCanvasRef.current) {
      // Canvas not ready yet
      return;
    }
    
    const canvas = fabricCanvasRef.current;

    // Setting up fabric canvas events

    // Force disable selection when not in select mode
    if (currentTool !== 'select') {
      // Disabling selection for drawing mode
      canvas.selection = false;
      canvas.isDrawingMode = false;
      canvas.discardActiveObject();
      
      // Keep skipTargetFind false to allow optical choice detection
      canvas.skipTargetFind = false;
      
      // Make sure all objects are non-interactive (except optical choices)
      canvas.forEachObject((obj) => {
        const objName = obj.get('name') as string;
        const isUserDrawing = (obj as any).isUserDrawing === true;
        
        // Skip user drawings - they already have correct settings
        if (isUserDrawing) {
          return;
        }
        
        // Keep optical choices interactive even in drawing mode
        // Check for both naming conventions (old and new)
        if (objName?.startsWith('questionOpticalChoice_') || objName?.startsWith('opticalChoice_')) {
          obj.set({
            selectable: false,
            evented: true // Keep optical choices clickable
          });
        } else {
          obj.set({
            selectable: false,
            evented: false
          });
        }
      });
      
      canvas.requestRenderAll();
    } else {
    
      canvas.skipTargetFind = false;
      return; // Don't attach drawing events in select mode
    }

    // Special handling for area eraser
    if (currentTool === 'area-eraser') {
      const handleAreaEraserDown = (options: any) => {
        const pointer = canvas.getPointer(options.e);
        startDrawing({ fabricPointer: pointer, type: 'fabric', e: options.e });
      };

      const handleAreaEraserMove = (options: any) => {
        if (isDrawing) {
          const pointer = canvas.getPointer(options.e);
          continueDrawing({ fabricPointer: pointer, type: 'fabric', e: options.e });
        }
      };

      const handleAreaEraserUp = (options: any) => {
        if (isDrawing) {
          const pointer = canvas.getPointer(options.e);
          endDrawing({ fabricPointer: pointer, type: 'fabric', e: options.e });
        }
      };

      canvas.on('mouse:down', handleAreaEraserDown);
      canvas.on('mouse:move', handleAreaEraserMove);
      canvas.on('mouse:up', handleAreaEraserUp);

      return () => {
        canvas.off('mouse:down', handleAreaEraserDown);
        canvas.off('mouse:move', handleAreaEraserMove);
        canvas.off('mouse:up', handleAreaEraserUp);
      };
    } else {
      // Regular drawing tools
      const handleMouseDown = (options: any) => {
        // Debug: Log mouse down event
      
        
        // Check if clicked on optical choice
        const target = options.target;
        if (target) {
          const targetName = target.get?.('name') || (target as any).name;
        
          
          // If optical choice clicked, don't start drawing
          if (targetName?.includes('optical') || targetName?.includes('questionOpticalChoice')) {
           
            // Trigger the click event on the target
            if (target.fire) {
              target.fire('mousedown', { e: options.e });
            }
            return;
          }
        }
        
        if (!isDrawingModeEnabled) return;
        
        // Check if clicking on an optical choice
        if (options.target) {
          const targetName = options.target.get('name') as string;
          if (targetName?.startsWith('opticalChoice_') || targetName?.startsWith('questionOpticalChoice_')) {
         
            return; // Let the optical choice handle the click
          }
        }
        
        // Check palm rejection using drawing hook - only for PointerEvent
        let pointerEvent: PointerEvent | undefined;
        if (options.e && 'pointerId' in options.e) {
          pointerEvent = options.e as PointerEvent;
          if (!shouldAllowInput(pointerEvent)) {          
            return;
          }
        }
        
        // Prevent default fabric selection behavior
        options.e.preventDefault();
        options.e.stopPropagation();
        
        const pointer = canvas.getPointer(options.e);
        
        // Pass the original event with fabric pointer
        const syntheticEvent = {
          ...options.e,
          fabricPointer: pointer,
          type: 'fabric'
        };
        
        startDrawing(syntheticEvent);
      };

      const handleMouseMove = (options: any) => {
        if (!isDrawing) return;
        
        // Check palm rejection for move events too
        let pointerEvent: PointerEvent | undefined;
        if (options.e && 'pointerId' in options.e) {
          pointerEvent = options.e as PointerEvent;
          if (!shouldAllowInput(pointerEvent)) {
            return;
          }
        }
        
        const pointer = canvas.getPointer(options.e);
        const syntheticEvent = {
          ...options.e,
          fabricPointer: pointer,
          type: 'fabric'
        };
        
        continueDrawing(syntheticEvent);
      };

      const handleMouseUp = (options: any) => {
     
        const pointer = canvas.getPointer(options.e);
        const syntheticEvent = {
          ...options.e,
          fabricPointer: pointer,
          type: 'fabric'
        };
        endDrawing(syntheticEvent);
      };

      const handleMouseOut = (options: any) => {
        if (isDrawing) {
          // Mouse left canvas while drawing - ending drawing
          const pointer = canvas.getPointer(options.e);
          const syntheticEvent = {
            ...options.e,
            fabricPointer: pointer,
            type: 'fabric'
          };
          endDrawing(syntheticEvent);
        }
      };

      // Attach fabric canvas events
      canvas.on('mouse:down', handleMouseDown);
      canvas.on('mouse:move', handleMouseMove);
      canvas.on('mouse:up', handleMouseUp);
      canvas.on('mouse:out', handleMouseOut);
      
   
  
      
   

      return () => {
        canvas.off('mouse:down', handleMouseDown);
        canvas.off('mouse:move', handleMouseMove);
        canvas.off('mouse:up', handleMouseUp);
        canvas.off('mouse:out', handleMouseOut);
        canvas.off('object:removed');
        canvas.off('object:added');
      };
    }
  }, [canvasReady, isDrawingModeEnabled, currentTool, isDrawing, startDrawing, continueDrawing, endDrawing]);

  // Native pointer event handlers (for pen support and palm rejection)
  const handlePointerDown = (e: React.PointerEvent) => {
    if (!isDrawingModeEnabled || currentTool === 'select') return;
    
    // Check if we're clicking on an optical choice
    const target = e.target as HTMLElement;
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (fabricCanvasRef.current && canvasRect) {
      const pointer = {
        x: e.clientX - canvasRect.left,
        y: e.clientY - canvasRect.top
      };
      const objects = fabricCanvasRef.current.getObjects();
      for (const obj of objects) {
        const objName = obj.get('name') as string;
        if (objName?.startsWith('opticalChoice_') || objName?.startsWith('questionOpticalChoice_')) {
          // Check if pointer is within object bounds
          if (obj.containsPoint(new FabricPoint(pointer.x, pointer.y))) {
                return; // Let fabric handle the event
          }
        }
      }
    }
    
    // Check palm rejection using drawing hook
    if (!shouldAllowInput(e.nativeEvent)) {
    
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // In pen mode, prevent all default actions to avoid UI interactions
    e.preventDefault();
    e.stopPropagation();
    
    // Handle all pointer types through the drawing handlers
    // which will properly check for palm rejection
    startDrawing(e.nativeEvent);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDrawing) return;
    
    // Check palm rejection for move events
    if (!shouldAllowInput(e.nativeEvent)) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    continueDrawing(e.nativeEvent);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (currentTool !== 'select') {
      e.preventDefault();
      e.stopPropagation();
    }
    endDrawing(e.nativeEvent);
  };
  
  const handlePointerCancel = (e: React.PointerEvent) => {
    if (currentTool !== 'select') {
      e.preventDefault();
      e.stopPropagation();
    }
    endDrawing(e.nativeEvent);
  };

  // Touch event handlers with swipe detection
  const handleTouchStartWrapper = (e: React.TouchEvent) => {
    if (!isDrawingModeEnabled || currentTool === 'select') return;
    
    // Check if we're touching an optical choice
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (fabricCanvasRef.current && canvasRect) {
        const pointer = {
          x: touch.clientX - canvasRect.left,
          y: touch.clientY - canvasRect.top
        };
        const objects = fabricCanvasRef.current.getObjects();
        for (const obj of objects) {
          const objName = obj.get('name') as string;
          if (objName?.startsWith('opticalChoice_') || objName?.startsWith('questionOpticalChoice_')) {
            // Check if touch is within object bounds
            if (obj.containsPoint(new FabricPoint(pointer.x, pointer.y))) {
           
              return; // Let fabric handle the event
            }
          }
        }
      }
    }
    
    handleTouchStart(e as any);
    
    if (e.touches.length === 1) {
      // Record swipe start position for mobile navigation
      if (isMobile && currentTool !== 'select') {
        swipeStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          time: Date.now()
        };
      }
      startDrawing(e.nativeEvent);
    }
  };

  const handleTouchMoveWrapper = (e: React.TouchEvent) => {
    handleTouchMove(e as any);
    
    if (e.touches.length === 1 && isDrawing) {
      continueDrawing(e.nativeEvent);
    }
  };

  const handleTouchEndWrapper = (e: React.TouchEvent) => {
    handleTouchEnd(e as any);
    
    if (e.touches.length === 0) {
      // Check for swipe gesture on mobile
      if (isMobile && currentTool !== 'select' && swipeStartRef.current && e.changedTouches.length > 0) {
        const touch = e.changedTouches[0];
        const deltaX = touch.clientX - swipeStartRef.current.x;
        const deltaY = touch.clientY - swipeStartRef.current.y;
        const deltaTime = Date.now() - swipeStartRef.current.time;
        
        // Check if it's a horizontal swipe
        if (Math.abs(deltaX) > Math.abs(deltaY) && // More horizontal than vertical
            Math.abs(deltaX) > MIN_SWIPE_DISTANCE && // Minimum distance
            deltaTime < MAX_SWIPE_TIME && // Quick enough
            !isDrawing) { // Not drawing
          
          if (deltaX > 0 && onSwipeRight) {
            // Swipe right - previous question
            onSwipeRight();
          } else if (deltaX < 0 && onSwipeLeft) {
            // Swipe left - next question
            onSwipeLeft();
          }
        }
        
        swipeStartRef.current = null;
      }
      
      endDrawing(e.nativeEvent);
    }
  };

  // Update optical choice visuals based on state
  useEffect(() => {
    if (!fabricCanvasRef.current || !isOpticalMode || !currentQuestionId) return;
    
    const canvas = fabricCanvasRef.current;
    
    // Update all optical choices for current question
    canvas.getObjects().forEach(obj => {
      const objName = (obj as any).name || '';
      // Check for both old and new naming conventions
      if (objName.startsWith('opticalChoice_') || objName.startsWith('questionOpticalChoice_')) {
        // For new implementation, choiceLabel is stored directly on the object
        const choiceLabel = (obj as any).choiceLabel;
        // Also check if this choice belongs to the current question
        const objQuestionId = (obj as any).questionId;
        
        if (!choiceLabel || (objQuestionId && objQuestionId !== currentQuestionId)) return;
        
        // Handle both Group (old) and Ellipse (new) implementations
        if (obj instanceof fabric.Group) {
          // Old implementation - Group with children
          const items = obj.getObjects ? obj.getObjects() : (obj as any)._objects || [];
          if (items.length >= 1) {
            const bgCircle = items[0] as any;
            
            // Check if choice is selected
            const isSelected = opticalAnswers[currentQuestionId] === choiceLabel;
            // Check if choice is eliminated
            const isEliminated = eliminatedChoices[currentQuestionId]?.includes(choiceLabel);
            
            if (isSelected) {
              // Selected state - orange highlight
              bgCircle.set({
                fill: 'rgba(251, 146, 60, 0.3)',
                stroke: '#fb923c',
                strokeWidth: 2
              });
            } else if (isEliminated) {
              // Eliminated state - no visual changes
              bgCircle.set({
                fill: 'transparent',
                stroke: 'transparent',
                strokeWidth: 0
              });
            } else {
              // Normal state - transparent
              bgCircle.set({
                fill: 'transparent',
                stroke: 'transparent',
                strokeWidth: 0
              });
            }
          }
        } else if (obj instanceof fabric.Ellipse) {
          // New implementation - Direct Ellipse object
          // Check if choice is selected
          const isSelected = opticalAnswers[currentQuestionId] === choiceLabel;
          // Check if choice is eliminated
          const isEliminated = eliminatedChoices[currentQuestionId]?.includes(choiceLabel);
          
          if (isSelected) {
            // Selected state - orange highlight
            obj.set({
              fill: 'rgba(251, 146, 60, 0.3)',
              stroke: '#fb923c',
              strokeWidth: 2
            });
          } else if (isEliminated) {
            // Eliminated state - no visual changes
            obj.set({
              fill: 'transparent',
              stroke: 'transparent',
              strokeWidth: 0
            });
          } else {
            // Normal state - transparent
            obj.set({
              fill: 'transparent',
              stroke: 'transparent',
              strokeWidth: 0
            });
          }
        }
      }
    });
    
    canvas.requestRenderAll();
  }, [isOpticalMode, currentQuestionId, opticalAnswers, eliminatedChoices]);

  // Force correct mode on mount - last resort check
  useEffect(() => {
    if (!fabricCanvasRef.current) return;
    
    // Multiple delayed checks to ensure correct mode
    const timers = [
      setTimeout(() => {
        const canvas = fabricCanvasRef.current;
        if (canvas && currentTool !== 'select') {
        
          canvas.selection = false;
          canvas.skipTargetFind = false; // Keep false for optical choices
        }
      }, 1000),
      
      setTimeout(() => {
        const canvas = fabricCanvasRef.current;
        if (canvas && currentTool !== 'select') {
      
          canvas.selection = false;
          canvas.skipTargetFind = false; // Keep false for optical choices
          canvas.requestRenderAll();
        }
      }, 2000)
    ];
    
    return () => timers.forEach(timer => clearTimeout(timer));
  }, [currentTool]); // Re-run when tool changes

  // Handle optical choice click from direct interaction
  const handleOpticalChoiceClick = useCallback((choice: string, questionId: string) => {
    if (onOpticalAnswerSelect) {
      onOpticalAnswerSelect(choice, questionId);
    }
  }, [onOpticalAnswerSelect]);
  
  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    canvas: fabricCanvasRef.current,
    canvasElement: canvasRef.current,
    questionMetadata: questionMetadataRef.current,
    clearCanvas: () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      
      // Get all objects
      const allObjects = canvas.getObjects();
      const objectsToRemove: fabric.Object[] = [];
      
      // Filter objects to remove (keep protected objects)
      allObjects.forEach(obj => {
        const objName = (obj as any).name || '';
        const objType = obj.type || '';
        
        // Skip protected question elements
        if (objName === 'questionBackground' || 
            objName === 'parentQuestionBackground' ||
            objName === 'questionFrame' ||
            objName === 'questionResizeHandle' ||
            objName === 'questionMoveHandle' ||
            objName === 'questionMoveIcon' ||
            objName?.startsWith('opticalChoice_') ||
            objName?.startsWith('opticalChoiceFeedback_') ||
            objName?.startsWith('opticalQuestionNumber_') ||
            objName?.startsWith('questionOpticalChoice_')) {
          return;
        }
        
        // Also check erasable property
        if ((obj as any).erasable === false) {
          return;
        }
        
        // Remove all paths, lines, shapes that are user-drawn
        if (objType === 'path' || objType === 'line' || objType === 'rect' || 
            objType === 'circle' || objType === 'ellipse' || objType === 'polygon' ||
            objType === 'group' || (obj as any).strokeId || (obj as any).id) {
          objectsToRemove.push(obj as any);
        }
      });
      
      // Remove objects
      objectsToRemove.forEach(obj => {
        canvas.remove(obj as any);
      });
      
      canvas.requestRenderAll();
    },
    exportCanvas: (format = 'png') => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return null;
      
      return canvas.toDataURL({
        format: format as any,
        quality: 1,
        multiplier: 2
      });
    }
  }), []);

  return (
    <div 
      ref={containerRef}
      className={`whiteboard-canvas-container ${className}`}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        msUserSelect: 'none',
        backgroundColor: '#ffffff',
        cursor: currentTool === 'select' ? 'default' : `url('data:image/svg+xml;utf8,<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="%23333"/></svg>') 3 21, crosshair`
      }}
      onMouseEnter={() => setIsHoveringCanvas(true)}
      onMouseLeave={() => setIsHoveringCanvas(false)}
    >
      <canvas
        id="whiteboard-canvas"
        ref={canvasRef}
        onTouchStart={handleTouchStartWrapper}
        onTouchMove={handleTouchMoveWrapper}
        onTouchEnd={handleTouchEndWrapper}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerEnter={(e) => {
          // Check if pen is detected
          if (e.pointerType === 'pen') {
            useWhiteboardStore.getState().setPenMode(true);
          }
          setIsHoveringCanvas(true);
        }}
        onPointerLeave={(e) => {
          useWhiteboardStore.getState().setPenMode(false);
          setIsHoveringCanvas(false);
          // Also end drawing if we're drawing when pointer leaves
          if (isDrawing) {
            // Pointer left canvas while drawing - ending drawing via onPointerLeave
            endDrawing(e.nativeEvent);
          }
        }}
        style={{
          touchAction: currentTool === 'select' ? 'auto' : 'none',
          display: 'block',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          cursor: currentTool === 'select' ? 'default' : `url('data:image/svg+xml;utf8,<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="%23333"/></svg>') 3 21, crosshair`
        }}
      />
      
      {/* Loading overlay */}
      {!canvasReady && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div className="loading-spinner">Loading...</div>
        </div>
      )}
    </div>
  );
};

// Custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps: WhiteboardCanvasProps, nextProps: WhiteboardCanvasProps) => {
  // Always re-render if canvas ready state changes
  if (prevProps.canvasReady !== nextProps.canvasReady) return false;
  
  // Always re-render if question changes
  if (prevProps.currentQuestionId !== nextProps.currentQuestionId) return false;
  if (prevProps.currentQuestion?.questionId !== nextProps.currentQuestion?.questionId) return false;
  
  // Re-render if drawing-related props change
  if (prevProps.currentTool !== nextProps.currentTool) return false;
  if (prevProps.currentColor !== nextProps.currentColor) return false;
  if (prevProps.currentStrokeWidth !== nextProps.currentStrokeWidth) return false;
  if (prevProps.isDrawingModeEnabled !== nextProps.isDrawingModeEnabled) return false;
  
  // Re-render if optical answers change for current question
  const currentQuestionId = prevProps.currentQuestionId;
  if (currentQuestionId) {
    if (prevProps.opticalAnswers?.[currentQuestionId] !== nextProps.opticalAnswers?.[currentQuestionId]) return false;
    if (prevProps.eliminatedChoices?.[currentQuestionId] !== nextProps.eliminatedChoices?.[currentQuestionId]) return false;
  }
  
  // Otherwise, props are equal
  return true;
};

export const WhiteboardCanvas = React.memo(forwardRef(WhiteboardCanvasComponent), arePropsEqual);
WhiteboardCanvas.displayName = 'WhiteboardCanvas';